import React, { useState, useEffect } from 'react';
import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { Button, useToast, useBreakpointValue, IconButton, Tooltip, useDisclosure, Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FiUser, FiLogOut } from 'react-icons/fi';
import { FaWallet } from 'react-icons/fa';
import MetaMaskConnect from './MetaMaskConnect';

const ConnectButton: React.FC = () => {
  const { address, isConnected } = useAccount();
  const { connect, connectors, isLoading, pendingConnector } = useConnect();
  const { disconnect } = useDisconnect();
  const toast = useToast();
  const { t } = useTranslation('common');
  const [isConnecting, setIsConnecting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // 响应式配置
  const isMobile = useBreakpointValue({ base: true, md: false });

  // 处理连接
  const handleConnect = async () => {
    // 移动端使用MetaMask专用弹出框，桌面端使用原有逻辑
    if (isMobile) {
      onOpen();
      return;
    }

    setIsConnecting(true);
    try {
      const metaMaskConnector = connectors.find(c => c.id === 'metaMask');
      if (metaMaskConnector) {
        await connect({ connector: metaMaskConnector });
        toast({
          title: t('success.connected'),
          description: 'MetaMask 钱包已成功连接',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('error.connectFailed'),
          description: '未找到MetaMask钱包',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      // 连接错误
      toast({
        title: t('error.connectFailed'),
        description: '请确保已安装MetaMask并切换到BSC测试网',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsConnecting(false);
    }
  };

  // 处理断开连接
  const handleDisconnect = () => {
    disconnect();
    toast({
      title: t('success.disconnected'),
      description: 'MetaMask 钱包连接已断开',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  // 格式化地址显示
  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  if (isConnected && address) {
    // 移动端显示图标按钮，桌面端显示完整按钮
    if (isMobile) {
      return (
        <>
          <Tooltip label={`${t('disconnectWallet')}: ${formatAddress(address)}`} placement="bottom">
            <IconButton
              icon={
                <FaWallet />
              }
              variant="outline"
              colorScheme="orange"
              onClick={handleDisconnect}
              size="sm"
              aria-label={t('disconnectWallet')}
            />
          </Tooltip>
          {/* 移动端MetaMask连接弹出框 */}
          <MetaMaskConnect isOpen={isOpen} onClose={onClose} />
        </>
      );
    }
    
    return (
      <Button
        leftIcon={
          <FaWallet />
        }
        variant="outline"
        colorScheme="orange"
        onClick={handleDisconnect}
        size="sm"
      >
        {formatAddress(address)}
      </Button>
    );
  }

  // 移动端显示图标按钮，桌面端显示完整按钮
  if (isMobile) {
    return (
      <>
        <Tooltip label={t('connectWallet')} placement="bottom">
          <IconButton
            icon={
              <FaWallet />
            }
            colorScheme="orange"
            onClick={handleConnect}
            isLoading={isConnecting || isLoading}
            size="sm"
            aria-label={t('connectWallet')}
          />
        </Tooltip>
        
        {/* 移动端MetaMask连接弹出框 */}
        <MetaMaskConnect isOpen={isOpen} onClose={onClose} />
      </>
    );
  }

  return (
    <Button
      leftIcon={
        <FaWallet />
      }
      colorScheme="orange"
      onClick={handleConnect}
      isLoading={isConnecting || isLoading}
      loadingText={t('loading')}
      size="sm"
    >
      {t('connectWallet')}
    </Button>
  );
};

export default ConnectButton; 